import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { BodyText, getCloudinaryUrl, Image, Radio, theme } from '@sg/garnish';
import { DressingWeight } from '@sg/graphql-schema';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../../../constants';
import { CustomizationIngredientNotice } from '../../../CustomizationIngredientNotice';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsDressingWeightV2 = (props: Props) => {
  const {
    ingredient,
    selectedWeight,
    isDisabled,
    size = 'md',
    restrictionNotice,
    restrictionNoticeVariation = 'icon',
    setDressingWeight,
  } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const { id, asset, name } = ingredient;

  const formattedImageUrl = getCloudinaryUrl(asset.url, { crop: 'scale', width: INGREDIENT_CARD_IMAGE_WIDTH }); // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onWeightChange = useCallback(
    (weight: DressingWeight) => {
      setDressingWeight(id, weight);
    },
    [id, setDressingWeight],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View>
      <View style={styles.header}>
        <Image
          source={formattedImageUrl}
          style={size === 'md' ? styles.imageMD : styles.imageLG}
        />

        <BodyText sizeMatch={['18']} style={styles.heading}>
          {name}
        </BodyText>

        {restrictionNotice ? (
          <CustomizationIngredientNotice
            notice={restrictionNotice}
            ingredientName={name}
            variation={restrictionNoticeVariation}
          />
        ) : null}
      </View>

      <Radio.Group
        itemsPerRow={3}
        gap={size === 'md' ? theme.spacing['2'] : theme.spacing['4']}
      >
        <Radio.Item<DressingWeight>
          value={DressingWeight.Light}
          isSelected={selectedWeight === DressingWeight.Light}
          onPress={onWeightChange}
          label={formatMessage(messages.label, { weight: DressingWeight.Light })} // prettier-ignore
          accessibilityLabel={formatMessage(messages.a11yLabel, { weight: DressingWeight.Light })} // prettier-ignore
          isDisabled={isDisabled}
          size={size === 'lg' ? 'md' : 'sm'}
          palette="spinach"
        />

        <Radio.Item<DressingWeight>
          value={DressingWeight.Medium}
          isSelected={selectedWeight === DressingWeight.Medium}
          onPress={onWeightChange}
          label={formatMessage(messages.label, { weight: DressingWeight.Medium })} // prettier-ignore
          accessibilityLabel={formatMessage(messages.a11yLabel, { weight: DressingWeight.Medium })} // prettier-ignore
          isDisabled={isDisabled}
          size={size === 'lg' ? 'md' : 'sm'}
          palette="spinach"
        />

        <Radio.Item<DressingWeight>
          value={DressingWeight.Heavy}
          isSelected={selectedWeight === DressingWeight.Heavy}
          onPress={onWeightChange}
          label={formatMessage(messages.label, { weight: DressingWeight.Heavy })} // prettier-ignore
          accessibilityLabel={formatMessage(messages.a11yLabel, { weight: DressingWeight.Heavy })} // prettier-ignore
          isDisabled={isDisabled}
          size={size === 'lg' ? 'md' : 'sm'}
          palette="spinach"
        />
      </Radio.Group>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  label: {
    defaultMessage: `{weight, select,
      LIGHT {Light}
      MEDIUM {Medium}
      HEAVY {Heavy}
      other {}
    }`,
    description: 'Customization | Dressing details | Dressing weight label',
  },
  a11yLabel: {
    defaultMessage: `{weight, select,
      LIGHT {Select light weight}
      MEDIUM {Select medium weight}
      HEAVY {Select heavy weight}
      other {}
    }`,
    description:
      'Customization | Dressing details | Dressing weight a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
    rowGap: theme.spacing['1'],
    marginBottom: theme.spacing['2'],
  },
  imageMD: {
    width: 40,
    height: 40,
    borderRadius: 40,
  },
  imageLG: {
    width: 64,
    height: 64,
    borderRadius: 64,
  },
  heading: {
    color: theme.colors.KALE,
    flexShrink: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  ingredient: { id: string; name: string; asset: { url: string } };
  selectedWeight: DressingWeight;
  isDisabled?: boolean;
  size?: 'md' | 'lg';
  restrictionNotice?: string;
  restrictionNoticeVariation?: 'label' | 'icon';
  setDressingWeight: (
    ingredientId: string,
    dressingWeight: DressingWeight,
  ) => void;
};
